import {
  completeTransactionMutation,
  startTransactionMutation,
} from "@/api/gql/fiskaly";
import { dexieDB, IDexieUntypedTransaction } from "@/services/dexie-database";
import { LogLevel, LogService } from "@/services/logging";
import { ITransaction } from "@/types/db/transaction";
import { PaymentMethodName } from "@/types/gql/generated/graphql-badges";
import FiskalyModule from "@/store/modules/fiskaly";
import { SyncStatus } from "@/types/db/product-slot-booking";
import { initiateTransactionsSync } from "@/services/sync-service";
import Dexie from "dexie";

const MAX_TRANSACTION_SYNC_ATTEMPTS = 10;

/**
 * Save a transaction to Dexie. Sets syncStatus to Pending so
 * it will be picked up by the sync job.
 */
export async function saveTransaction(transaction: ITransaction) {
  const dexieTransaction: IDexieUntypedTransaction = {
    uuid: transaction.uuid,
    cashfloatUuid: transaction.cashfloatUuid,
    bookingUuid: transaction.bookingUuid,
    createdAtTimestamp: transaction.createdAtTimestamp,
    transaction,
    syncStatus: SyncStatus.Pending,
    attempts: 0,
  };
  try {
    LogService.info(
      `Fiskaly: Storing transaction into Dexie ${transaction.uuid}`,
      { transaction }
    );
    await dexieDB.untypedTransactions.put(dexieTransaction);
    // Trigger sync job
    initiateTransactionsSync();
  } catch (err) {
    LogService.info(
      `Fiskaly: Error storing transaction into Dexie ${transaction.uuid}`,
      { err },
      LogLevel.Error
    );
  }
}

/**
 * Query transactions by cashfloat UUID from Dexie.
 * Used during cash point closure to retrieve all transactions for the current cashfloat.
 */
export async function getTransactionsByCashfloatUuid(
  cashfloatUuid: string
): Promise<ITransaction[]> {
  try {
    const records = await dexieDB.untypedTransactions
      .where("cashfloatUuid")
      .equals(cashfloatUuid)
      .toArray();
    return records.map((r) => r.transaction);
  } catch (err) {
    LogService.info(
      `Fiskaly: Error querying transactions by cashfloatUuid ${cashfloatUuid}`,
      { err },
      LogLevel.Error
    );
    return [];
  }
}

/**
 * Query a single transaction by booking UUID from Dexie.
 */
export async function getTransactionByBookingUuid(
  bookingUuid: string
): Promise<ITransaction | null> {
  try {
    const record = await dexieDB.untypedTransactions
      .where("bookingUuid")
      .equals(bookingUuid)
      .first();
    return record ? record.transaction : null;
  } catch (err) {
    LogService.info(
      `Fiskaly: Error querying transaction by bookingUuid ${bookingUuid}`,
      { err },
      LogLevel.Error
    );
    return null;
  }
}

/**
 * Sync pending/failed transactions from Dexie to backend.
 * Processes one at a time: start mutation -> complete mutation.
 * Marks as Synced on success, increments attempts on failure.
 */
export async function syncDexieTransactions(limit = 5): Promise<void> {
  if (!dexieDB.isOpen()) {
    LogService.info("Transaction Sync: Database not open, skipping");
    return;
  }

  let syncedCount = 0;

  while (syncedCount < limit) {
    let record: IDexieUntypedTransaction | undefined;

    try {
      // Priority: Pending first (newest), then Failed with attempts < max
      const pending = await dexieDB.untypedTransactions
        .where("[syncStatus+createdAtTimestamp]")
        .between(
          [SyncStatus.Pending, Dexie.minKey],
          [SyncStatus.Pending, Dexie.maxKey]
        )
        .reverse()
        .first();

      if (pending) {
        record = pending;
      } else {
        const failed = await dexieDB.untypedTransactions
          .where("[syncStatus+attempts]")
          .between(
            [SyncStatus.Failed, 0],
            [SyncStatus.Failed, MAX_TRANSACTION_SYNC_ATTEMPTS]
          )
          .first();

        if (failed) {
          record = failed;
        }
      }
    } catch (error) {
      LogService.info(
        `Transaction Sync: Error querying transactions`,
        { error },
        LogLevel.Error
      );
      break;
    }

    if (!record) {
      break;
    }

    const transaction = record.transaction as ITransaction;
    if (!transaction || !transaction.completeTransaction) {
      // Not ready to sync — skip
      syncedCount++;
      continue;
    }

    // Skip transactions that already have a qrcode (already completed by backend)
    if (transaction.qrcode) {
      try {
        record.syncStatus = SyncStatus.Synced;
        await dexieDB.untypedTransactions.put(record);
      } catch {
        // ignore
      }
      syncedCount++;
      continue;
    }

    // Mark as InProgress
    try {
      record.syncStatus = SyncStatus.InProgress;
      await dexieDB.untypedTransactions.put(record);
    } catch {
      // ignore
    }

    try {
      await syncSingleTransaction(record, transaction);

      // Mark as Synced
      record.syncStatus = SyncStatus.Synced;
      await dexieDB.untypedTransactions.put(record);

      // Remove from Vuex unsyncedTransactions
      FiskalyModule.removeUnsyncedTransaction({
        cashfloatUuid: transaction.cashfloatUuid,
        uuid: transaction.uuid,
      });

      LogService.info(
        `Transaction Sync: Successfully synced transaction ${transaction.uuid}`
      );
    } catch (error) {
      LogService.info(
        `Transaction Sync: Error syncing transaction ${transaction.uuid}`,
        { error },
        LogLevel.Error
      );
      // Mark as Failed, increment attempts
      record.syncStatus = SyncStatus.Failed;
      record.attempts = (record.attempts || 0) + 1;
      try {
        await dexieDB.untypedTransactions.put(record);
      } catch {
        // ignore
      }
    }

    syncedCount++;
  }
}

/**
 * Sync a single transaction: start mutation -> complete mutation.
 */
async function syncSingleTransaction(
  record: IDexieUntypedTransaction,
  transaction: ITransaction
): Promise<void> {
  // If not yet started (no signature), call startTransactionMutation
  if (!transaction.signature) {
    try {
      LogService.info(
        `Transaction Sync: Starting transaction ${transaction.uuid}`
      );
      const res = await startTransactionMutation({
        seqId: transaction.seqId,
        transactionUUID: transaction.uuid,
        cashfloatUid: transaction.cashfloatUuid,
        cashfloatSeqId: Number(transaction.cashfloatSeqId),
        bookingUUID: transaction.bookingUuid,
        posRegisterId: transaction.posRegisterId,
        groupId: Number(transaction.groupId),
        error: "Failed to sign transaction",
      });
      if (res) {
        transaction.signature = res.signature || "STARTED";
      }
    } catch (error: any) {
      LogService.info(
        `Transaction Sync: Couldn't start transaction ${transaction.uuid}`,
        { transaction },
        LogLevel.Error
      );
      // If it's a duplicate, don't retry — continue to complete
      let isDuplicate = false;
      if (error.graphQLErrors && error.graphQLErrors.length) {
        const extensions = error.graphQLErrors.map((e: any) => e.extensions);
        isDuplicate = extensions.some((e: any) => e.code === "DUPLICATE_ENTRY");
      }
      if (!isDuplicate) {
        throw error;
      }
    }
  }

  // Complete the transaction
  LogService.info(
    `Transaction Sync: Completing transaction ${transaction.uuid}`
  );
  const res = await completeTransactionMutation({
    bookingUUID: String(transaction.bookingUuid),
    transactionUUID: transaction.uuid,
    seqId: transaction.seqId,
    cashfloatSeqId: Number(transaction.cashfloatSeqId),
    posRegisterId: String(transaction.posRegisterId),
    groupId: Number(transaction.groupId),
    receiptNumber: transaction.receiptNumber,
    payments: transaction.completeTransaction!.payments.map((p) => {
      return {
        ...p,
        type: p.type as unknown as PaymentMethodName,
      };
    }),
    items: transaction.completeTransaction!.items,
    error: "Failed to sign transaction",
    completedAt: transaction.completedAt || "",
  });
  if (res) {
    transaction.qrcode = res.qrCodeData || null;
  }
}
